import { useCallback, useState } from 'react';
import { useFormHook, useSetError } from '@Hooks/index';
import { useUpdateBusinessAddress } from '@Hooks/api/useUpdateBusinessAddress';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/index';
import { OnboardingFormFields } from '@Types/index';
import { useSyncBusinessAddressSlice } from '@Hooks/useSyncBusinessAddressSlice';
import { getStateDropdownItem, nonUsRefineState } from '@Components/inputs/AddressStateDropDown';
import { useGetCountries } from '@Hooks/api/useGetCountries';
import { nonUsRefineZip } from '@Components/inputs/ZipAddress';
import { RoutesMap } from '@Router/router.types';
import { useValidateAutoComplete } from '@Hooks/providers/useValidateAutoComplete';
import { useNonUsCountryDefaultValues } from '@Hooks/custom/useNonUsCountryDefaultValues';
import {
  createNonUsBuisnessAddressSchema,
  UsBusinessAddressSchema,
} from '../businessAddressForm/businessAddressSchema';
import { useChangeCountryBusiness } from './useChangeCountryBusiness';
import type { CombinedAddressForm } from '@Hooks/custom/useNonUsCountryDefaultValues';
import type { Country } from '@Types/country';
import type { IBusinessAddressForm } from '../businessAddressForm/businessAddressSchema';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { UseFormResetField } from 'react-hook-form';
import type { IBusinessAdress } from '@Types/address';

export const useBusinessAddressLogic = () => {
  const {
    customer: {
      customerData: { businessAddressData },
    },
    getIsUsFlow,
  } = useBoundStore((state) => state);
  const isUS = getIsUsFlow();
  const { line1, line2, postalCode, state, city, sameAsPrimary, country } = businessAddressData;
  const { data } = useGetCountries(true, false);
  const countryObject = data?.countries.find((countryItem) => countryItem.code === country);
  const usCountry = isUS ? data?.countries.find((c) => c.code === 'US') : undefined;
  const NonUSPostalCode = countryObject ? postalCode : '';
  const nonUSCity = countryObject ? city : '';
  const [isDisableValidateAutoComplete, setIsDisableValidateAutoComplete] = useState(false);
  const nonUsState = countryObject ? getStateDropdownItem(state, countryObject) : null;

  const [defaultValues, setDefaultValues] = useState({
    [OnboardingFormFields.LINE1]: line1 || '',
    [OnboardingFormFields.APARTMENT]: line2 || '',
    [OnboardingFormFields.CITY]: !isUS ? nonUSCity : (city ?? ''),
    [OnboardingFormFields.STATE]: !isUS
      ? nonUsState
      : (getStateDropdownItem(state, usCountry) ?? { title: '', value: '' }),
    [OnboardingFormFields.ZIP]: !isUS ? NonUSPostalCode : (postalCode ?? ''),
    [OnboardingFormFields.COUNTRY]: countryObject || undefined,
    [OnboardingFormFields.BUSINESS_ADDRESS_SAME_AS_PRIMARY]: sameAsPrimary ?? false,
  });

  const nonUsSchema = createNonUsBuisnessAddressSchema();
  const finalNonUsSchema = nonUsRefineZip(nonUsRefineState(nonUsSchema));

  const BusinessAddressSchema = isUS ? UsBusinessAddressSchema : finalNonUsSchema;
  const { navigateToNextPage } = useNavigationManager();

  const formMethods: ExtendedUseFormReturn<IBusinessAddressForm> = useFormHook({
    formSchema: BusinessAddressSchema,
    formDefaultValues: defaultValues as IBusinessAddressForm,
    schemaTitle: 'businessAddressSchema',
    mode: 'onTouched',
  });

  const { syncBusinessAddress } = useSyncBusinessAddressSlice();
  const { mutateAsync: asyncUpdateBusiness, isPending } = useUpdateBusinessAddress((_, input) => {
    syncBusinessAddress(input);
    navigateToNextPage(RoutesMap.BUSINESS_ADDRESS);
  });

  const handleSubmit = useCallback(async () => {
    const formData = formMethods.getValues();
    asyncUpdateBusiness(formData as IBusinessAdress);
    syncBusinessAddress(formData as IBusinessAdress);
  }, [formMethods, asyncUpdateBusiness]);

  const { watch, clearErrors, resetField, setValue } = formMethods;
  const selectedCountry: Country | null | undefined = watch(OnboardingFormFields.COUNTRY);
  const subDivisions = selectedCountry?.subDivisions;
  // Reset errors, zip, state, and city when the selected country is changed
  const isSameAddress = formMethods.watch(OnboardingFormFields.BUSINESS_ADDRESS_SAME_AS_PRIMARY);

  useChangeCountryBusiness({
    country,
    city,
    clearErrors,
    isSameAddress,
    postalCode,
    resetField,
    selectedCountry,
    setValue,
    state,
    setIsDisableValidateAutoComplete,
  });

  useSetError({ formMethods });

  const countries = !isUS ? data?.countries || [] : undefined;
  const countryList = countries?.map((countryObjectItem) => ({
    title: countryObjectItem.name,
    value: countryObjectItem.code,
    ...countryObjectItem,
  }));
  const usSubDivisions = isUS ? usCountry?.subDivisions : subDivisions;
  const states = usSubDivisions?.map((subDivision) => ({
    title: subDivision.name,
    value: subDivision.code,
  }));

  useNonUsCountryDefaultValues({
    isUS,
    countriesData: data,
    customerCountry: country,
    customerState: state,
    defaultValues: defaultValues as CombinedAddressForm,
    resetField: resetField as UseFormResetField<CombinedAddressForm>,
    setDefaultValues: setDefaultValues as (values: CombinedAddressForm) => void,
  });
  useValidateAutoComplete({
    formMethods,
    relevantFields: [
      OnboardingFormFields.CITY,
      OnboardingFormFields.STATE,
      OnboardingFormFields.ZIP,
      OnboardingFormFields.LINE1,
      OnboardingFormFields.APARTMENT,
    ],
    isDisableValidateAutoComplete,
    setIsDisableValidateAutoComplete,
  });
  return {
    handleSubmit,
    formMethods,
    isPending,
    defaultValues,
    states,
    selectedCountry,
    countryList,
    isUS,
  };
};
